import { Button, Typography } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFreeTrialSharedInfo } from '../../../../api/utils/free-trial/api';
import {
  FREE_TRIAL_RESULT_TYPE_MAP,
  FreeTrialSharedInfo,
} from '../../../../constants/types/free-trial/type';
import FloatingBarFrame from '../../../../components/home/floating-bar/FloatingBarFrame';
import { FREE_TRIAL_EXTERNAL_LINKS } from '../../../../constants/urlconfig';
import GuestFreeTrialModal from '../../../../components/home/floating-bar/GuestFreeTrialModal';

const SCHOOL_POINTS = [
  {
    number: 1,
    title: '정철의 검증된 학습법',
    description: '소리 · 뜻 · 그림을 함께 익히는 탄탄한 커리큘럼',
  },
  {
    number: 2,
    title: '발명특허 삽화기억술',
    description:
      '그림 한 컷에 문장 한 덩어리, 문장을 못 만드는 아이도 암송해요',
  },
  {
    number: 3,
    title: '성경이 교재예요',
    description: '영어와 함께 말씀을 마음에 새기는 신앙 교육',
  },
];

const TRIAL_STEPS = [
  {
    number: 1,
    title: '수업하기',
    description: '엄마와 함께',
  },
  {
    number: 2,
    title: '온라인 학습',
    description: '아이 혼자',
  },
  {
    number: 3,
    title: 'AI 인증',
    description: '말로 확인',
  },
];

const FreeTrialShare = () => {
  const navigate = useNavigate();

  const { shareToken } = useParams<{ shareToken: string }>();

  const [sharedInfo, setSharedInfo] = useState<FreeTrialSharedInfo | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [showFloatingApplyCta, setShowFloatingApplyCta] = useState(false);

  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingApplyCta(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!shareToken) {
      setIsLoading(false);
      setIsError(true);
      return;
    }

    let isMounted = true;

    const fetchSharedInfo = async () => {
      try {
        setIsLoading(true);
        setIsError(false);

        const data = await getFreeTrialSharedInfo(shareToken);

        if (!isMounted) return;

        setSharedInfo({
          childName: data.childName,
          recitationType: data.recitationType,
        });
      } catch (error) {
        console.error('무료체험 공유 정보 조회 실패', error);

        if (!isMounted) return;

        setIsError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchSharedInfo();

    return () => {
      isMounted = false;
    };
  }, [shareToken]);

  const openBlogReviewLink = () => {
    window.open(FREE_TRIAL_EXTERNAL_LINKS.BLOG_REVIEW, '_blank');
  };

  const openPrimaryBlogReviewLink = () => {
    window.open(FREE_TRIAL_EXTERNAL_LINKS.PRIMARY_BLOG_REVIEW, '_blank');
  };

  const openYoutubeReviewLink = () => {
    window.open(FREE_TRIAL_EXTERNAL_LINKS.YOUTUBE_REVIEW, '_blank');
  };

  const openPrimaryYoutubeReviewLink = () => {
    window.open(FREE_TRIAL_EXTERNAL_LINKS.PRIMARY_YOUTUBE_REVIEW, '_blank');
  };

  const openSeminarApplyLink = () => {
    window.open(FREE_TRIAL_EXTERNAL_LINKS.SEMINAR_APPLY, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F7FC]">
        <Typography className="text-[13px] font-medium text-gray-900 md:text-[14px]">
          공유 결과를 불러오고 있어요.
        </Typography>
      </div>
    );
  }

  if (isError || !sharedInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F7FC] px-5">
        <div className="w-full max-w-[400px] rounded-2xl bg-white px-5 py-8 text-center shadow-[0_6px_20px_rgba(40,25,100,0.05)]">
          <Typography className="text-[22px] font-black text-gray-900 md:text-[24px]">
            공유 결과를 확인할 수 없어요
          </Typography>

          <Typography className="mt-2 text-[13px] leading-5 text-gray-700 md:text-[14px] md:leading-6">
            공유 링크가 올바른지 다시 확인해 주세요.
          </Typography>

          <Button
            ripple={false}
            onClick={() => navigate('/')}
            className="mt-6 h-11 w-full rounded-xl bg-[#6737F4] py-0 text-[13px] font-bold normal-case text-white shadow-none hover:bg-[#5425D6] hover:shadow-none focus:shadow-none"
          >
            홈페이지로 이동
          </Button>
        </div>
      </div>
    );
  }

  const result = FREE_TRIAL_RESULT_TYPE_MAP[sharedInfo.recitationType];

  return (
    <div className="min-h-screen bg-white pb-[74px] md:py-20 md:pt-10">
      <main className="mx-auto w-full max-w-[720px] overflow-hidden bg-white md:rounded-[28px] md:shadow-[0_8px_30px_rgba(40,25,100,0.08)]">
        {/* 헤더 */}
        <header className="flex h-14 items-center justify-between bg-white px-4 md:h-16 md:px-7">
          <img
            src={process.env.PUBLIC_URL + '/images/logo.svg'}
            alt="JEBS"
            className="h-6 w-auto md:h-7"
          />

          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]"
          >
            홈페이지 보기
          </button>
        </header>

        {/* 공유 결과 */}
        <section className="bg-[#F1EDFF] px-5 pb-9 pt-7 text-center md:px-10 md:pb-12 md:pt-10">
          <div className="inline-flex rounded-full bg-white px-3 py-1.5 text-[11px] font-bold text-[#6737F4] shadow-sm md:text-[12px]">
            친구가 결과를 공유했어요
          </div>

          <div className="mx-auto mt-2 flex h-[104px] w-[104px] items-center justify-center overflow-hidden rounded-full md:h-[124px] md:w-[124px]">
            <img
              src={process.env.PUBLIC_URL + result.image}
              alt={result.name}
              className="h-[130px] w-[130px] object-contain"
            />
          </div>

          <Typography className="mt-2 text-[13px] font-bold text-gray-700 md:text-[14px]">
            {FREE_TRIAL_RESULT_TYPE_MAP[sharedInfo.recitationType].catchphrase}
          </Typography>

          <Typography className="text-gray-950 mt-1 text-[32px] font-black leading-none md:text-[38px]">
            {FREE_TRIAL_RESULT_TYPE_MAP[sharedInfo.recitationType].name}!
          </Typography>

          <Typography className="mt-5 text-[20px] font-black text-[#6737F4] md:text-[24px]">
            우리 아이는 무슨 형일까요?
          </Typography>

          <Typography className="mx-auto mt-4 max-w-[440px] text-[13px] leading-6 text-gray-700 md:text-[14px]">
            그림으로 영어 말씀을 암송하고
            <br />
            AI 스피킹 인증까지 받아 보세요. 1레슨 무료예요.
          </Typography>
        </section>

        {/* 젭스 소개 */}
        <section className="bg-white px-4 py-7 md:px-8 md:py-10">
          <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
            젭스는 이런 곳이에요
          </Typography>

          <Typography className="text-gray-950 mt-2 text-[22px] font-black leading-[1.35] md:text-[28px]">
            말씀으로 배우는 영어,
            <br />
            정철영어성경학교
          </Typography>

          <div className="mt-5 flex flex-col gap-2.5 md:mt-6 md:grid md:grid-cols-3">
            {SCHOOL_POINTS.map((point) => (
              <div
                key={point.number}
                className="flex items-start gap-3 rounded-xl bg-[#F7F5FC] px-3 py-3.5 md:flex-col md:gap-2 md:px-4 md:py-4"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#6737F4] text-[13px] font-black text-white">
                  {point.number}
                </div>

                <div>
                  <Typography className="text-[13px] font-black text-gray-900 md:text-[14px]">
                    {point.title}
                  </Typography>

                  <Typography className="mt-0.5 text-[11px] leading-[1.6] text-gray-700 md:text-[12px]">
                    {point.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 암송 방식 */}
        <section className="bg-white px-4 py-7 md:px-8 md:py-10">
          <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
            이렇게 외워요
          </Typography>

          <div className="mt-3 overflow-hidden rounded-xl border border-[#DED8F2]">
            <img
              src={
                process.env.PUBLIC_URL +
                '/images/free-trial/story_main_for_tutorial.jpeg'
              }
              alt="영어 말씀 암송 그림"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>

          <Typography className="mt-3 text-[12px] leading-5 text-gray-700 md:text-[13px] md:leading-6">
            7을 짚으며 “일곱째 날”, 감은 눈을 짚으며 “쉬었어요”.&nbsp;
            <br className="hidden sm:block" />
            그림만 보고도 문장이 떠올라요.
          </Typography>

          {/* 무료체험 3단계 */}
          <div className="mt-7">
            <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
              무료체험 3단계
            </Typography>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {TRIAL_STEPS.map((step) => (
                <div
                  key={step.number}
                  className="rounded-xl bg-[#FFF5D9] px-2 py-4 text-center md:px-4 md:py-5"
                >
                  <Typography className="text-[22px] font-black leading-none text-[#A75900] md:text-[26px]">
                    {step.number}
                  </Typography>

                  <Typography className="mt-2 text-[12px] font-black text-gray-900 md:text-[13px]">
                    {step.title}
                  </Typography>

                  <Typography className="mt-1 text-[10px] text-gray-700 md:text-[11px]">
                    {step.description}
                  </Typography>
                </div>
              ))}
            </div>

            <Typography className="mt-3 text-[11px] leading-5 text-gray-700 md:text-[12px] md:leading-6">
              끝까지 하면 결과 리포트와 우리 아이 암송 캐릭터가 나와요.
            </Typography>
          </div>

          {/* 후기 */}
          <div className="mt-8 flex flex-col">
            <div className="flex-1">
              <Typography className="text-[11px] font-bold text-[#6737F4] md:text-[12px]">
                먼저 해 본 가정의 후기
              </Typography>

              <div className="mt-4 flex flex-col gap-2">
                {/* 블로그 후기 */}
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-xl p-2 text-left transition-colors duration-200 hover:bg-gray-100"
                  onClick={openPrimaryBlogReviewLink}
                >
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[#E8E5F1]">
                    <img
                      src="/svg/icon-blog-on.svg"
                      alt="네이버 블로그"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <Typography className="mb-1 text-[9px] font-bold text-green-500 md:text-[10px]">
                      NAVER BLOG
                    </Typography>

                    <Typography className="line-clamp-2 text-[12px] font-medium leading-[1.5] text-gray-700 md:text-[13px]">
                      젭스로 자란 아이가 중학생이 되면 어떨까요?
                    </Typography>
                  </div>
                </button>

                {/* 유튜브 후기 */}
                <button
                  type="button"
                  className="flex items-center gap-3 rounded-xl p-2 text-left transition-colors duration-200 hover:bg-gray-100"
                  onClick={openPrimaryYoutubeReviewLink}
                >
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[#E8E5F1]">
                    <img
                      src="/svg/icon-youtube-on.svg"
                      alt="유튜브"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <Typography className="mb-1 text-[9px] font-bold text-red-500 md:text-[10px]">
                      YOUTUBE
                    </Typography>

                    <Typography className="line-clamp-2 text-[12px] font-medium leading-[1.5] text-gray-700 md:text-[13px]">
                      남달랐던 영어 자신감, 말씀으로 배운 영어에 있었어요!
                    </Typography>
                  </div>
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <Button
                variant="outlined"
                ripple={false}
                onClick={openBlogReviewLink}
                className="h-10 rounded-xl border-[#B9A6FF] px-2 py-0 text-[12px] font-bold normal-case text-[#6737F4] shadow-none hover:bg-[#F8F5FF] hover:shadow-none focus:shadow-none"
              >
                블로그 후기
              </Button>

              <Button
                variant="outlined"
                ripple={false}
                onClick={openYoutubeReviewLink}
                className="h-10 rounded-xl border-[#B9A6FF] px-2 py-0 text-[12px] font-bold normal-case text-[#6737F4] shadow-none hover:bg-[#F8F5FF] hover:shadow-none focus:shadow-none"
              >
                유튜브 후기
              </Button>
            </div>
          </div>

          {/* CTA */}
          <Button
            ripple={false}
            onClick={openSeminarApplyLink}
            className="mt-7 h-12 w-full rounded-xl bg-[#F1EDFF] py-0 text-[13px] font-bold normal-case text-[#5B35DD] shadow-none transition-colors hover:bg-[#E9E2FF] hover:shadow-none focus:shadow-none"
          >
            설명회 신청
          </Button>

          <Typography className="mt-4 text-center text-[10px] leading-4 text-gray-700 md:text-[11px]">
            무료체험은 회원가입 후 신청할 수 있어요 · 계정당 2회
          </Typography>
        </section>
      </main>

      <FloatingBarFrame
        isVisible={showFloatingApplyCta}
        isScrolling={false}
        compactWidthClass="w-[calc(100%-24px)]"
      >
        <div className="rounded-[22px] bg-white p-1 shadow-[0_8px_24px_rgba(88,57,180,0.20)] lg:rounded-full">
          <Button
            ripple={false}
            onClick={() => setIsGuestModalOpen(true)}
            className="group relative flex h-[70px] w-full items-center justify-center gap-3 overflow-visible rounded-[18px] bg-[#6737F4] px-6 py-0 normal-case text-white shadow-none transition-all duration-300 hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none lg:h-[78px] lg:rounded-full lg:px-9"
          >
            {/* ring */}
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-[3px] animate-[free-trial-ring_2.8s_ease-in-out_infinite] rounded-[21px] border-2 border-[#A98EFF]/60 lg:rounded-full"
            />

            {/* shine */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden rounded-[18px] lg:rounded-full"
            >
              <span className="absolute -inset-y-4 -left-16 w-10 rotate-[18deg] animate-[free-trial-share-shine_2.6s_ease-in-out_infinite] bg-white/50 blur-md" />
            </span>

            <span className="relative z-10 whitespace-nowrap text-[17px] font-extrabold lg:text-[18px]">
              나도 무료체험 신청하기
            </span>

            <span className="relative z-10 shrink-0 whitespace-nowrap rounded-lg bg-[#FFD928] px-3 py-1.5 text-[11px] font-extrabold leading-none text-[#4B3500] lg:text-[12px]">
              1레슨 무료
            </span>
          </Button>
        </div>
      </FloatingBarFrame>

      <GuestFreeTrialModal
        open={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
      />
    </div>
  );
};

export default FreeTrialShare;
